import { useState, useCallback } from 'react';
import { UserPlus, Trash2 } from 'lucide-react';
import { ClientRole, type EventUser, type CreateEventUserInput } from '@peles/shared';
import { EntityList, StatusBadge, EntityModal } from '../common';
import type { EntityListColumn } from '../common';
import { useEventUsers, useAddEventUser, useRemoveEventUser } from '../../hooks/useEvents';
import { SearchableSelect } from './SearchableSelect';

interface EventUserListViewProps {
  eventId: string;
}

const CLIENT_ROLE_OPTIONS = Object.values(ClientRole).map((role) => ({
  value: role,
  label: role
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase()),
}));

function formatRole(role: ClientRole): string {
  return role
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatPermissions(permissions: EventUser['permissions']): string {
  const labels: string[] = [];
  if (permissions.canApprovePlan) labels.push('Plan');
  if (permissions.canApproveInvoice) labels.push('Invoice');
  if (permissions.canMakePayments) labels.push('Payments');
  if (permissions.canMakeChanges) labels.push('Changes');
  if (permissions.canPrint) labels.push('Print');
  return labels.length > 0 ? labels.join(', ') : '—';
}

const DEFAULT_PERMISSIONS = {
  canApprovePlan: false,
  canApproveInvoice: false,
  canMakePayments: false,
  canMakeChanges: false,
  canPrint: false,
};

export function EventUserListView({ eventId }: EventUserListViewProps) {
  const [showAdd, setShowAdd] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<ClientRole>(ClientRole.OTHER);
  const [permissions, setPermissions] = useState({ ...DEFAULT_PERMISSIONS });

  const { data: eventUsers = [], isLoading } = useEventUsers(eventId);
  const addEventUser = useAddEventUser(eventId);
  const removeEventUser = useRemoveEventUser(eventId);

  const resetForm = useCallback(() => {
    setSelectedUserId('');
    setSelectedRole(ClientRole.OTHER);
    setPermissions({ ...DEFAULT_PERMISSIONS });
  }, []);

  const handleAdd = useCallback(() => {
    if (!selectedUserId) return;
    const input: CreateEventUserInput = {
      userId: selectedUserId,
      clientRole: selectedRole,
      permissions,
    };
    addEventUser.mutate(input, {
      onSuccess: () => {
        setShowAdd(false);
        resetForm();
      },
    });
  }, [selectedUserId, selectedRole, permissions, addEventUser, resetForm]);

  const handleRemove = useCallback(
    (userId: string) => {
      removeEventUser.mutate(userId);
    },
    [removeEventUser],
  );

  const togglePermission = useCallback((key: keyof typeof DEFAULT_PERMISSIONS) => {
    setPermissions((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const columns: EntityListColumn<EventUser>[] = [
    {
      key: 'user',
      header: 'User',
      render: (item) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-primary-700">
              {item.userName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-gray-900 font-medium text-sm">{item.userName}</p>
            {item.userEmail && <p className="text-xs text-gray-500">{item.userEmail}</p>}
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (item) => <StatusBadge label={formatRole(item.clientRole)} color="blue" />,
    },
    {
      key: 'permissions',
      header: 'Permissions',
      render: (item) => (
        <span className="text-sm text-gray-600">{formatPermissions(item.permissions)}</span>
      ),
      hideOnMobile: true,
    },
    {
      key: 'actions',
      header: '',
      render: (item) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRemove(item.userId);
          }}
          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          aria-label="Remove client"
        >
          <Trash2 size={15} />
        </button>
      ),
      width: 'w-12',
      align: 'center',
    },
  ];

  return (
    <>
      <EntityList
        items={eventUsers}
        columns={columns}
        getKey={(item) => item.id}
        isLoading={isLoading}
        title="Clients"
        emptyMessage="No clients linked to this event."
        titleActions={
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
          >
            <UserPlus size={16} />
            Add Client
          </button>
        }
      />

      <EntityModal
        open={showAdd}
        onClose={() => {
          setShowAdd(false);
          resetForm();
        }}
        title="Add Client"
        size="sm"
        footer={
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                setShowAdd(false);
                resetForm();
              }}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={!selectedUserId || addEventUser.isPending}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {addEventUser.isPending ? 'Adding…' : 'Add'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
            <SearchableSelect
              entityType="user"
              value={selectedUserId}
              onChange={(v) => setSelectedUserId(typeof v === 'string' ? v : '')}
              placeholder="Search users…"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as ClientRole)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
            >
              {CLIENT_ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
            <div className="space-y-2">
              {(
                [
                  ['canApprovePlan', 'Approve Plan'],
                  ['canApproveInvoice', 'Approve Invoice'],
                  ['canMakePayments', 'Make Payments'],
                  ['canMakeChanges', 'Make Changes'],
                  ['canPrint', 'Allowed Print'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permissions[key]}
                    onChange={() => togglePermission(key)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </EntityModal>
    </>
  );
}
